/**
 * Whether a line on a dashboard says anything the reader does not already
 * see. A derived figure that is true by construction ("Top 2 are 100% of the
 * total" when the reader holds two things), a count of what is drawn right
 * below, or a subtitle that restates the title is left off the screen.
 */
import type {
  BreakdownPart,
  BoardSummary,
  Coverage,
  Drawn,
  Template,
} from '@/features/mitra/api/dashboard'

/** Lower-cased words, punctuation dropped: what two lines are compared on. */
const words = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}%]+/gu, ' ')
    .split(' ')
    .filter(Boolean)

/** Words that carry no meaning of their own when one line is held against another. */
const FILLER = new Set(['a', 'an', 'the', 'my', 'of', 'and', 'in', 'on', 'for', 'to', 'is', 'it'])

/**
 * A dashboard's purpose, where it adds to its name: null where it is empty,
 * where it is the stock sentence of the template the dashboard was made from
 * (the engine copies it in), or where every word of it is already in the name.
 * `templates` null means not known yet: a dashboard made from a template then
 * shows none rather than a line that may vanish a moment later.
 */
export function purposeShown(
  board: Pick<BoardSummary, 'name' | 'purpose' | 'template'>,
  templates: readonly Pick<Template, 'key' | 'purpose'>[] | null,
): string | null {
  const purpose = board.purpose.trim()
  if (!purpose) return null
  const said = words(purpose).join(' ')
  // A board made from a template never shows a purpose (owner, 2026-10-23):
  // boards saved before the engine stopped copying it still carry the old
  // stock sentence, which no longer matches the template's current one.
  if (board.template !== null) return null
  if (templates?.some((t) => words(t.purpose).join(' ') === said)) return null
  const name = new Set(words(board.name))
  const own = words(purpose).filter((w) => !FILLER.has(w))
  if (own.every((w) => name.has(w))) return null
  return purpose
}

/** Partial coverage is the only coverage worth a mark: every row counted is the norm. */
export function partial(coverage: Coverage | null | undefined): coverage is Coverage {
  return !!coverage && coverage.total > 0 && coverage.counted < coverage.total
}

/** Whether a widget has anything to flag under its value: rows left out, or a warning. */
export function hasMeta(widget: Pick<Drawn, 'coverage' | 'check'>): boolean {
  return partial(widget.coverage) || (widget.check?.warnings.length ?? 0) > 0
}

export interface ShareSegment {
  value: number
  tone: 'accent' | 'teal' | 'violet' | 'neutral'
  label: string
  percent: number
}

/** The biggest parts of a sum and the rest, each as a share of the whole. */
export interface Share {
  segments: ShareSegment[]
  topPercent: number
}

const TONES = ['accent', 'teal', 'violet'] as const

/**
 * How much of a sum its biggest parts make, or null where that says nothing:
 * two parts or fewer, the top parts being every part, the top making 99% or
 * more (the rest would read "Others 0%"), or a part below zero (a share of a
 * total that nets gains against losses is not a share). Worked from the parts'
 * own figures; drawing only.
 */
export function topShare(
  part: Pick<BreakdownPart, 'rows'>,
  top: readonly { label: string; value: string | null }[],
): Share | null {
  const numeric = part.rows
    .filter((r) => r.counted && r.value !== null)
    .map((r) => Number(r.value))
    .filter((n) => Number.isFinite(n))
  if (numeric.length <= 2 || top.length < 2 || top.length >= numeric.length) return null
  if (numeric.some((n) => n < 0)) return null
  const total = numeric.reduce((n, v) => n + v, 0)
  if (total <= 0) return null
  const segments: ShareSegment[] = top.map((row, index) => {
    const value = Number(row.value)
    return {
      value,
      tone: TONES[index] ?? 'neutral',
      label: row.label,
      percent: Math.round((value / total) * 100),
    }
  })
  const topSum = segments.reduce((n, seg) => n + seg.value, 0)
  const rest = total - topSum
  const restPercent = Math.round((rest / total) * 100)
  if (topSum / total >= 0.99 || restPercent < 1) return null
  segments.push({ value: rest, tone: 'neutral', label: 'Others', percent: restPercent })
  return { segments, topPercent: Math.round((topSum / total) * 100) }
}
